import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { LuActivity, LuMail, LuLock, LuUser, LuArrowRight } from 'react-icons/lu';
import { Link } from 'react-router-dom';

const AuthPage = ({ onLogin }) => {
  const registrationEnabled = process.env.REACT_APP_ALLOW_REGISTRATION === 'true';
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const emailRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setLoading(true);

    try {
      const response = isLogin
        ? await api.login({ email: formData.email, password: formData.password })
        : await api.register(formData);

      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      onLogin(response.data.user);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setFormError((typeof detail === 'string' ? detail : detail?.message) || 'Authentication failed');
      window.requestAnimationFrame(() => emailRef.current?.focus());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden bg-zinc-950">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-950 to-zinc-900" />
      
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
        backgroundSize: '64px 64px'
      }} />

      {/* Warm accent glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass-card rounded-2xl p-10 border border-zinc-800/50">
          {/* Logo - Minimal */}
          <div className="flex justify-center mb-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
                <LuActivity className="w-6 h-6 text-zinc-900" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-zinc-100">Cloud Watcher</h1>
                <p className="text-xs text-zinc-500">Infrastructure Intelligence</p>
              </div>
            </div>
          </div>

          {/* Tabs - Understated */}
          <div className="flex gap-1 mb-8 p-1 bg-zinc-900/50 rounded-lg">
            <button
              onClick={() => setIsLogin(true)}
              data-testid="login-tab"
              className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                isLogin 
                  ? 'bg-zinc-800 text-amber-400 shadow-sm' 
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              Sign In
            </button>
            {registrationEnabled && <button
              onClick={() => setIsLogin(false)}
              data-testid="register-tab"
              className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                !isLogin 
                  ? 'bg-zinc-800 text-amber-400 shadow-sm' 
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              Create Account
            </button>}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <label htmlFor="auth-name" className="block text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">
                  Full Name
                </label>
                <div className="relative">
                  <LuUser className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <input
                    id="auth-name"
                    type="text"
                    autoComplete="name"
                    data-testid="name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 outline-none transition-all"
                    placeholder="John Doe"
                    required={!isLogin}
                  />
                </div>
              </motion.div>
            )}

            <div>
              <label htmlFor="auth-email" className="block text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">
                Email
              </label>
              <div className="relative">
                <LuMail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input
                  id="auth-email"
                  ref={emailRef}
                  type="email"
                  autoComplete="email"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 outline-none transition-all"
                  placeholder="you@company.com"
                  required
                />
              </div>
              {isLogin && (
                <div className="mt-2 text-right">
                  <Link to="/forgot-password" className="text-xs text-amber-400 hover:text-amber-300">Forgot password?</Link>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="auth-password" className="block text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">
                Password
              </label>
              <div className="relative">
                <LuLock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input
                  id="auth-password"
                  type="password"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  data-testid="password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 outline-none transition-all"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {formError && <p role="alert" className="rounded-lg border border-red-500/25 bg-red-500/10 p-3 text-sm text-red-200">{formError}</p>}
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full py-3.5 mt-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-zinc-900 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/20"
            >
              {loading ? (
                <><div className="w-5 h-5 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" aria-hidden /><span>{isLogin ? 'Signing in…' : 'Creating account…'}</span></>
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <LuArrowRight className="w-4 h-4" />
                </>
              )}
            </motion.button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-zinc-800/50">
            {registrationEnabled ? (
              <p className="text-center text-xs text-zinc-600">
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-amber-400 hover:text-amber-300 font-medium transition-colors"
                >
                  {isLogin ? 'Create one' : 'Sign in'}
                </button>
              </p>
            ) : (
              <p className="text-center text-xs text-zinc-600">Access is invitation-only.</p>
            )}
          </div>
        </div>

        {/* Trust indicators */}
        <div className="mt-6 flex items-center justify-center gap-6 text-xs text-zinc-600">
          <span>Multi-cloud</span>
          <span className="w-1 h-1 rounded-full bg-zinc-700" />
          <span>Real-time monitoring</span>
          <span className="w-1 h-1 rounded-full bg-zinc-700" />
          <span>Cost optimization</span>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
